import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { PageHero } from "@/components/page-hero";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
const HERO =
  "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1600&q=72&auto=format&fit=crop";

export const Route = createFileRoute("/location-gender-reveal")({
  head: () => ({
    meta: [
      {
        title: "Gender reveal Vaucluse & Provence | DJ Val",
      },
      {
        name: "description",
        content:
          metaDesc("Gender reveal dans le Vaucluse, Avignon et Aix : canons à fumée rose ou bleue, étincelles froides, sono et DJ. Forfait clé en main dès 290 €.")
      },
      {
        property: "og:title",
        content: "Gender Reveal Provence — Effets pro & sono | DJ Val",
      },
      {
        property: "og:description",
        content:
          metaDesc("Canons à fumée rose/bleue, étincelles froides, sono, animation DJ. Vaucluse, Bouches-du-Rhône, Gard. Forfait dès 290 €.")
      },
      { property: "og:url", content: "https://dj-val.fr/location-gender-reveal" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: HERO },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/location-gender-reveal" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": "https://dj-val.fr/location-gender-reveal/#service",
              name: "Gender Reveal — organisation, effets & DJ",
              serviceType: "Gender Reveal Party",
              provider: { "@id": "https://dj-val.fr/#business" },
              areaServed: [
                "Vaucluse", "Bouches-du-Rhône", "Gard", "Avignon",
                "Cavaillon", "Aix-en-Provence", "Salon-de-Provence",
                "Arles", "Nîmes", "Marseille",
              ].map((name) => ({ "@type": "City", name })),
              description:
                "Forfait clé en main pour gender reveal en Provence : canons à fumée rose ou bleue, étincelles froides, sono Pioneer/RCF, micro, animation musicale. Livraison et installation incluses.",
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "EUR",
                lowPrice: "290",
                highPrice: "1490",
                offerCount: "3",
                availability: "https://schema.org/InStock",
              },
              url: "https://dj-val.fr/location-gender-reveal",
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "Combien coûte un gender reveal organisé par un pro en Provence ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "À partir de 290 € pour la formule « effets seuls » (1 canon à fumée colorée + livraison/installation/reprise dans un rayon de 80 km autour de Cavaillon). Avec sono + micro pour l'annonce : 490 €. Avec DJ animation jusqu'à 4 h : 890 €. Devis personnalisé sous 24 h.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Canon à fumée ou étincelles froides : que choisir pour un gender reveal ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Les canons à fumée colorée (rose ou bleu) sont l'effet le plus photogénique et le plus utilisé : nuage dense pendant 6 à 10 secondes, parfait en extérieur. Les étincelles froides (cold sparks) ajoutent une touche cérémonielle, sans chaleur, utilisables en intérieur. Les deux se combinent très bien pour une annonce à effet maximum.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Les fumigènes rose/bleu sont-ils autorisés en extérieur en France ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Oui, à condition d'utiliser des fumigènes pyrotechniques agréés CE (catégorie F2 ou F3 selon la quantité), à distance des matières inflammables, hors période de sécheresse renforcée et hors arrêté préfectoral interdisant les feux. Nous fournissons du matériel agréé, l'attestation d'assurance RC Pro et appliquons un protocole sécurité strict (extincteur, périmètre, vent).",
                  },
                },
                {
                  "@type": "Question",
                  name: "Pouvez-vous organiser un gender reveal en intérieur ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Oui. En intérieur nous utilisons des étincelles froides (cold sparks, sans flamme, sans chaleur, certifiées intérieur) ou des canons à confettis rose/bleu. Les fumigènes classiques sont déconseillés en intérieur — nous les remplaçons systématiquement par une alternative sûre.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Vous intervenez dans quelles villes pour un gender reveal ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Cavaillon, Avignon, Aix-en-Provence, Salon-de-Provence, Pertuis, Apt, Carpentras, Arles, Nîmes, Marseille et toute la Provence (Vaucluse, Bouches-du-Rhône, Gard, Drôme, Var). Forfait livraison inclus jusqu'à 80 km autour de Cavaillon, frais kilométriques au-delà.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Combien de temps à l'avance faut-il réserver ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Idéalement 3 à 4 semaines à l'avance, surtout en haute saison (mai à septembre). Les week-ends partent vite. Demande possible en urgence sous 7 jours selon disponibilité.",
                  },
                },
              ],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
                { "@type": "ListItem", position: 2, name: "Location matériel", item: "https://dj-val.fr/location-materiel" },
                { "@type": "ListItem", position: 3, name: "Gender Reveal", item: "https://dj-val.fr/location-gender-reveal" },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const FORMULES = [
  {
    name: "Effet seul",
    price: "290 €",
    sub: "Livré · installé · repris",
    inc: [
      "1 canon à fumée colorée (rose OU bleu)",
      "Déclenchement à distance",
      "Livraison + reprise (80 km Cavaillon)",
      "Briefing sécurité sur place",
    ],
    cta: "Idéal pour une annonce courte filmée par un proche",
  },
  {
    name: "Effet + Sono",
    price: "490 €",
    sub: "Pour réunir 30 à 60 invités",
    inc: [
      "1 canon à fumée colorée",
      "Sono RCF 800 W + micro HF Shure",
      "Playlist de votre choix",
      "Branchement micro pour annonce",
      "Livraison + installation + reprise",
    ],
    cta: "Le bon équilibre pour la majorité des annonces",
    highlight: true,
  },
  {
    name: "Pack complet + DJ",
    price: "890 €",
    sub: "Annonce + soirée 4 h",
    inc: [
      "2 canons à fumée + étincelles froides",
      "Sono pro + éclairage LED",
      "DJ Val sur place (animation 4 h)",
      "Coordination du moment-clé",
      "Confettis rose ou bleu en bonus",
    ],
    cta: "Pour transformer l'annonce en vraie fête",
  },
];

const EFFETS = [
  {
    title: "Canon à fumée colorée",
    text: "L'effet emblématique : un nuage dense rose ou bleu pendant 6 à 10 secondes. Spectaculaire en photo et vidéo, idéal en extérieur (jardin, terrasse, vignes, plage).",
  },
  {
    title: "Étincelles froides (cold sparks)",
    text: "Geyser d'étincelles certifié intérieur — sans flamme, sans chaleur. Effet « feu d'artifice de mariage » sécurisé pour les annonces en salle.",
  },
  {
    title: "Canon à confettis",
    text: "Confettis biodégradables rose ou bleu projetés à 8 mètres. Parfait en complément d'un canon à fumée ou comme alternative en intérieur.",
  },
  {
    title: "Sono + micro pour l'annonce",
    text: "Sonorisation Pioneer/RCF + micro HF Shure pour diffuser une musique forte au moment du déclenchement et permettre aux futurs parents de prendre la parole.",
  },
];

const ETAPES = [
  { n: "01", t: "Brief 15 min", d: "Date, lieu, nombre d'invités, intérieur/extérieur, ambiance souhaitée." },
  { n: "02", t: "Devis sous 24 h", d: "Forfait clair, tout inclus (matériel, livraison, installation, reprise, assurance)." },
  { n: "03", t: "Repérage", d: "Validation des accès, prise électrique, contraintes vent/sécurité si extérieur." },
  { n: "04", t: "Jour J", d: "Arrivée 1 à 2 h avant, installation discrète, briefing déclenchement, captation possible." },
];

function Page() {
  useReveal();

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={HERO}
        eyebrow="Vaucluse · Provence · Sud-Est"
        title={<><span className="italic">Gender Reveal</span><span className="block">l'annonce qui se voit & s'entend</span></>}
        subtitle="Canons à fumée rose ou bleue, étincelles froides, sono pro et animation DJ. Matériel agréé, sécurité maîtrisée, photos & vidéos garanties spectaculaires. Forfait clé en main dès 290 €."
        primary={{ label: "Demander un devis 24 h", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
      />
      <ReviewsProof />


      {/* TLDR */}
      <div className="px-6">
        <TldrBox
          title="En bref"
          summary="DJ Val propose un service complet de gender reveal en Provence (Vaucluse, Bouches-du-Rhône, Gard) : canons à fumée rose/bleue, étincelles froides, sono et animation DJ. 3 formules de 290 € à 890 €, matériel pyrotechnique agréé CE, assurance RC Pro, livraison/installation incluses."
          items={[
            { label: "À partir de", value: "290 € (effet seul livré/installé)" },
            { label: "Zone", value: "Cavaillon, Avignon, Aix, Salon, Nîmes, Marseille" },
            { label: "Effets", value: "Fumée colorée · Étincelles froides · Confettis" },
            { label: "Réservation", value: "3 à 4 semaines à l'avance recommandé" },
          ]}
        />
      </div>

      {/* FORMULES */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">3 formules</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Choisissez votre <span className="italic">moment d'annonce</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {FORMULES.map((f, i) => (
            <div
              key={f.name}
              className={`border p-8 flex flex-col sr ${
                f.highlight
                  ? "border-[var(--gold)] bg-[var(--gold)]/5"
                  : "border-foreground/10"
              }`}
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {f.highlight && (
                <p className="eyebrow text-[var(--gold)] mb-3">Le plus demandé</p>
              )}
              <h3 className="font-serif-display text-2xl text-foreground">{f.name}</h3>
              <p className="font-serif-display italic gold-text-shine text-4xl mt-3">
                {f.price}
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-2">
                {f.sub}
              </p>
              <span className="gold-rule !w-8 my-6" />
              <ul className="space-y-2 text-sm font-light flex-1">
                {f.inc.map((it) => (
                  <li key={it} className="flex gap-2">
                    <span className="text-[var(--gold)]">·</span>
                    {it}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-muted-foreground italic">{f.cta}</p>
              <Link
                to="/contactez-nous"
                className="mt-6 block text-center bg-[var(--gold)] text-foreground py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
              >
                Réserver cette formule
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* EFFETS DISPONIBLES */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 sr" data-reveal>
            <p className="eyebrow text-foreground">Le matériel</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Effets <span className="italic">disponibles</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid gap-8 sm:grid-cols-2">
            {EFFETS.map((e, i) => (
              <div
                key={e.title}
                className="border border-foreground/10 p-8 sr"
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <p className="font-serif-display italic text-foreground text-xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif-display text-2xl text-foreground mt-3">
                  {e.title}
                </h3>
                <span className="gold-rule !w-8 my-4" />
                <p className="text-sm leading-relaxed font-light">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHODE */}
      <section className="py-24 px-6 max-w-5xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">Notre méthode</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Du brief au <span className="italic">moment d'annonce</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-px bg-foreground/10 border border-foreground/10">
          {ETAPES.map((p, i) => (
            <div
              key={p.t}
              className="bg-background p-8 grid md:grid-cols-[80px_1fr] gap-6 sr"
              data-reveal
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <p className="font-serif-display italic text-[var(--gold)] text-3xl">
                {p.n}
              </p>
              <div>
                <h3 className="font-serif-display text-xl text-foreground">{p.t}</h3>
                <p className="text-sm leading-relaxed font-light mt-2">{p.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITE */}
      <section className="py-20 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Sécurité &amp; conformité</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Du matériel <span className="italic">pro</span>, pas du bricolage Amazon
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <div className="mt-10 grid sm:grid-cols-3 gap-6 text-sm text-left">
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">CE F2/F3</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">
                Fumigènes et étincelles certifiés CE, manipulés par un opérateur formé.
              </p>
            </div>
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">RC Pro</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">
                Assurance Hiscox couvrant l'effet pyrotechnique et le matériel sono.
              </p>
            </div>
            <div className="border border-foreground/10 p-6">
              <p className="font-serif-display text-[var(--gold)] text-xl">Protocole</p>
              <p className="text-xs text-muted-foreground mt-2 font-light">
                Périmètre sécurité, extincteur sur site, contrôle vent, alternative
                intérieure prête en cas de météo défavorable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ visible (mirror du JSON-LD) */}
      <section className="py-24 px-6 max-w-3xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-foreground">FAQ</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Questions <span className="italic">fréquentes</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {[
            {
              q: "Combien coûte un gender reveal en Provence ?",
              a: "Dès 290 € pour l'effet seul livré/installé, 490 € avec sono + micro, 890 € avec DJ pendant 4 h. Devis personnalisé sous 24 h en fonction de la distance et des effets choisis.",
            },
            {
              q: "Canon à fumée ou étincelles froides ?",
              a: "Fumée colorée pour l'extérieur (effet photo le plus spectaculaire), étincelles froides pour l'intérieur (sans flamme, sans chaleur). Les deux se combinent.",
            },
            {
              q: "C'est légal en extérieur ?",
              a: "Oui avec du matériel CE agréé, hors période de sécheresse renforcée et hors arrêté préfectoral. Nous gérons la conformité de A à Z.",
            },
            {
              q: "Vous venez jusqu'à Marseille / Nîmes / Aix ?",
              a: "Oui — Vaucluse, Bouches-du-Rhône, Gard, Drôme, Var. 80 km autour de Cavaillon inclus, frais kilométriques au-delà.",
            },
            {
              q: "Délai de réservation ?",
              a: "3 à 4 semaines à l'avance recommandé, surtout d'avril à septembre. Urgence possible sous 7 jours selon disponibilité.",
            },
          ].map((f) => (
            <details
              key={f.q}
              className="border border-foreground/10 p-6 group [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer flex justify-between items-start gap-4 text-foreground font-serif-display text-lg">
                {f.q}
                <span className="text-[var(--gold)] text-2xl leading-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-4 text-sm font-light leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="py-16 px-6 bg-card border-t border-foreground/10">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">À combiner avec</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/location-materiel"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Location de sono
            </Link>
            <Link
              to="/sono-lumiere"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Sono & lumière pro
            </Link>
            <Link
              to="/soirees-evenements"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Soirées privées
            </Link>
            <Link
              to="/dj-mariage"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              DJ Mariage
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Prêt à <span className="italic gold-text-shine">révéler la surprise</span> ?
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm font-light leading-relaxed">
            Dites-nous date, lieu et nombre d'invités — vous recevez un devis ferme sous 24 h.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contactez-nous"
              className="bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Demander mon devis
            </Link>
            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "gender_reveal" })}
              className="border border-foreground/20 text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-foreground transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
